import { Link } from "react-router-dom";
import { CONTACT_EMAIL } from "@/data/contact";
import { Bot, ArrowLeft, Trash2, Mail, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";

// Public account-deletion page. Required by the Google Play / App Store
// account-deletion policy: a web URL, discoverable without installing the app,
// that lets users request deletion of their account + associated data. The
// in-app path lives in Settings → Danger Zone (gdpr.py::/delete-account); this
// page documents it and gives an email fallback for users who uninstalled.
const AccountDeletionPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Supprimer votre compte — Stronger Bot"
        description="Comment supprimer définitivement votre compte Stronger Bot et toutes les données associées, depuis l'application ou par e-mail."
        canonical="/account-deletion"
      />

      <nav className="fixed top-0 left-0 right-0 z-40 glass">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-bold text-white">
              Stronger Bot
            </span>
          </Link>
          <Link to="/">
            <Button
              variant="ghost"
              className="text-slate-400 hover:text-white gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Accueil
            </Button>
          </Link>
        </div>
      </nav>

      <main className="pt-24 pb-20 px-4 max-w-3xl mx-auto">
        <h1 className="font-heading text-3xl font-bold text-white mb-2">
          Supprimer votre compte Stronger Bot
        </h1>
        <p className="text-slate-400 text-sm mb-10">
          Éditeur : Stronger Bot · {CONTACT_EMAIL}
        </p>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-3 flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-primary" />
            Depuis l'application (recommandé)
          </h2>
          <p className="text-slate-400 leading-relaxed">
            Connectez-vous, puis ouvrez{" "}
            <strong className="text-slate-200">
              Paramètres → Zone de danger → Supprimer mon compte
            </strong>
            . La suppression est immédiate et définitive.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-3 flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            Sans l'application
          </h2>
          <p className="text-slate-400 leading-relaxed">
            Si vous avez désinstallé l'application, envoyez un e-mail à{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}?subject=Suppression%20de%20compte`}
              className="text-primary underline underline-offset-2"
            >
              {CONTACT_EMAIL}
            </a>{" "}
            depuis l'adresse e-mail de votre compte, avec l'objet « Suppression
            de compte ». Nous traitons la demande sous 30 jours.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-3 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-primary" />
            Données supprimées
          </h2>
          <div className="text-slate-400 leading-relaxed space-y-2">
            <p>
              La suppression efface définitivement : votre compte et votre
              profil, vos conversations, les documents que vous avez importés,
              et l'ensemble des données personnelles associées.
            </p>
            <p>
              Aucune donnée n'est conservée au-delà du nécessaire ; des journaux
              techniques minimaux peuvent subsister brièvement pour des raisons
              de sécurité, puis sont purgés. Aucune donnée n'est vendue à des
              tiers.
            </p>
          </div>
        </section>

        <p className="text-slate-400 text-sm">
          Voir aussi notre{" "}
          <Link to="/privacy" className="text-primary underline underline-offset-2">
            politique de confidentialité
          </Link>
          .
        </p>
      </main>

      <footer className="border-t border-white/5 py-8 text-center text-slate-400 text-sm">
        <p>© Stronger Bot</p>
        <div className="flex justify-center gap-6 mt-3">
          <Link to="/privacy" className="hover:text-white transition-colors">
            Confidentialité
          </Link>
          <Link to="/terms" className="hover:text-white transition-colors">
            CGU
          </Link>
        </div>
      </footer>
    </div>
  );
};
export default AccountDeletionPage;
